'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics and Preeclampsia: The Plasticiser Connection"
      author="MicroPlastics Editorial Team"
      date="March 12, 2026"
      readTime="10 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics and preeclampsia, the plasticiser connection"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Preeclampsia, high blood pressure and protein in the urine after
        20 weeks of pregnancy, affects <strong>5–8% of pregnancies</strong>{' '}
        worldwide and rates are rising. Recent research links higher
        maternal exposure to <strong>PFAS</strong>, <strong>BPA</strong>,
        and <strong>phthalates</strong> (the plasticisers that travel on
        microplastic particles) to increased preeclampsia risk through
        impaired placental development and endothelial dysfunction.
        Reduction strategy: filter drinking water (especially for PFAS),
        eliminate plastic in food prep and storage, switch from non-stick
        cookware, audit cosmetics. Always coordinate with your OB or
        maternal-fetal medicine specialist.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Preeclampsia affects 5-8% of pregnancies; rates have risen ~25% in the US over two decades.</>,
          <>The disease is rooted in abnormal placental development in early pregnancy, with symptoms emerging later.</>,
          <>PFAS exposure (especially PFOA, PFOS) is linked to higher preeclampsia risk in multiple human cohort studies.</>,
          <>BPA and phthalates impair trophoblast invasion, the process that builds proper placental blood supply.</>,
          <>Microplastics in placental tissue (UNM 2024: 100% of placentas) overlap with the affected tissue itself.</>,
          <>Reduction is precautionary: filter water for PFAS, switch off non-stick pans, eliminate plastic in food heat contact.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What preeclampsia actually is
      </h2>
      <p>
        Preeclampsia is a pregnancy complication defined by:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>New high blood pressure</strong> after 20 weeks (systolic ≥140 or diastolic ≥90 mmHg)</li>
        <li><strong>Protein in the urine</strong> (proteinuria) OR signs of organ dysfunction (kidney, liver, low platelets, headache, vision changes)</li>
      </ul>
      <p>
        Untreated, preeclampsia can progress to eclampsia (seizures) and
        HELLP syndrome (life-threatening blood and liver dysfunction). It is
        the leading cause of maternal mortality in many countries and a
        major cause of preterm delivery, low birth weight, and intrauterine
        growth restriction.
      </p>
      <p>
        Crucially, although symptoms appear in the second half of pregnancy,
        preeclampsia originates much earlier, during placental formation in
        the first trimester. The root cause is incomplete <em>trophoblast
        invasion</em>: the cells that should reshape maternal blood vessels
        to deliver large amounts of blood to the placenta don&apos;t fully
        do so. Later, the placenta becomes stressed, releases inflammatory
        factors into maternal circulation, and triggers the systemic
        disease.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How plasticisers fit in
      </h2>
      <BlogComparisonTable
        caption="Plasticisers and preeclampsia, research summary"
        columns={[
          { key: 'chemical', label: 'Chemical' },
          { key: 'mechanism', label: 'Proposed mechanism' },
          { key: 'evidence', label: 'Human evidence' },
        ]}
        rows={[
          { chemical: 'PFAS (PFOA, PFOS)', mechanism: 'Endothelial dysfunction; oxidative stress; thyroid disruption', evidence: 'Multiple cohorts show higher maternal serum PFAS associated with higher preeclampsia risk' },
          { chemical: 'BPA', mechanism: 'Impaired trophoblast invasion; oxidative stress in placental cells', evidence: 'Case-control studies link higher BPA to preeclampsia' },
          { chemical: 'Phthalates (DEHP, DBP)', mechanism: 'Reduced placental angiogenesis; altered estrogen signaling', evidence: 'Cohort studies link metabolite levels to gestational hypertension' },
          { chemical: 'Cadmium (often on plastics)', mechanism: 'Placental zinc displacement; reduced trophoblast function', evidence: 'Linked to preeclampsia in cohort studies' },
          { chemical: 'Microplastic particles', mechanism: 'Direct placental inflammation; oxidative stress', evidence: 'No direct human study yet; animal studies show placental dysfunction' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why PFAS gets special attention
      </h2>
      <p>
        Per- and polyfluoroalkyl substances (PFAS) are the &ldquo;forever
        chemicals&rdquo; used in non-stick cookware, water- and
        stain-resistant fabrics, fast-food wrappers, and many industrial
        applications. They persist in the body for years (PFOA half-life ~3
        years; PFOS ~5 years), meaning exposure before pregnancy carries
        forward.
      </p>
      <p>
        PFAS evidence for preeclampsia is among the strongest plasticiser
        signals:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>The <strong>C8 Health Project</strong> (PFOA-exposed Ohio Valley cohort) showed elevated preeclampsia rates.</li>
        <li>Multiple Scandinavian and Chinese cohorts replicate the association across PFOS, PFOA, and newer PFAS like GenX.</li>
        <li>The mechanism (endothelial dysfunction and oxidative stress) directly mirrors preeclampsia pathophysiology.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Risk factors you can&apos;t change vs. ones you can
      </h2>
      <BlogComparisonTable
        caption="Preeclampsia risk factors"
        columns={[
          { key: 'category', label: 'Category' },
          { key: 'factors', label: 'Factors' },
        ]}
        rows={[
          { category: 'Non-modifiable', factors: 'First pregnancy, age >35 or <20, family history, prior preeclampsia, multiple gestation, chronic kidney disease' },
          { category: 'Partially modifiable', factors: 'Chronic hypertension, diabetes, obesity, autoimmune disease, IVF pregnancy' },
          { category: 'Modifiable, medical', factors: 'Low-dose aspirin if high risk (start ~12 weeks per ACOG), calcium supplementation, prenatal care attendance' },
          { category: 'Modifiable, lifestyle', factors: 'Diet quality, exercise, stress, sleep' },
          { category: 'Modifiable, environmental', factors: 'PFAS exposure (water + non-stick cookware), BPA (food packaging), phthalates (fragrance, vinyl), air pollution' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Cutting plasticisers before and during pregnancy
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Filter water for PFAS.</strong> Standard pitcher filters do not remove most PFAS. You need: granular activated carbon block (Clearly Filtered, Epic, AquaTru), reverse osmosis (AquaTru, Waterdrop), or ion exchange (specific PFAS-rated cartridges). See <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">filter comparison</Link>.
        </li>
        <li>
          <strong>Stop using non-stick cookware.</strong> PTFE (Teflon) coatings release PFAS at high heat. Switch to stainless steel, cast iron, or enameled cast iron. See <Link href="/blog/best-non-toxic-cookware" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">non-toxic cookware ranked</Link>.
        </li>
        <li>
          <strong>Eliminate microwaving and hot food contact with plastic.</strong> A 2023 study found microwaving plastic releases up to 4.22 million microplastic particles per cm² in 3 minutes, plus migrating plasticisers.
        </li>
        <li>
          <strong>Switch food storage to glass.</strong> Pyrex, Anchor, Weck. Around $40-80 for a starter set.
        </li>
        <li>
          <strong>Skip fragranced products.</strong> &ldquo;Fragrance/parfum&rdquo; on a label often hides phthalates. Choose fragrance-free or essential-oil-only.
        </li>
        <li>
          <strong>Avoid fast-food wrappers and microwave popcorn bags.</strong> Both are common PFAS sources.
        </li>
        <li>
          <strong>Filter indoor air.</strong> HEPA in bedroom; open windows daily; remove shoes at the door (tracked-in particles).
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Medical preventive steps to discuss with your OB
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Low-dose aspirin (81 mg)</strong> from ~12 weeks if you have one major or two moderate preeclampsia risk factors (ACOG guidance).</li>
        <li><strong>Calcium supplementation</strong> if dietary calcium is low (under 600 mg/day).</li>
        <li><strong>Blood pressure monitoring at home</strong> in the second half of pregnancy if you have any risk factors.</li>
        <li><strong>Earlier and more frequent prenatal visits</strong> if you have a history of preeclampsia.</li>
        <li><strong>Vitamin D status check</strong>, deficiency is associated with preeclampsia.</li>
      </ul>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-pregnancy-by-trimester" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          pregnancy by trimester
        </Link>,{' '}
        <Link href="/blog/microplastics-in-placenta" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in the placenta
        </Link>, and{' '}
        <Link href="/blog/microplastics-and-miscarriage-risk" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics and miscarriage risk
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Baby/kid product material, glass, stainless, silicone, polypropylene, PPSU.</>,
          <>Packaging type, jar vs pouch vs multi-layer plastic.</>,
          <>Brand and product line, clean certifications flagged.</>,
          <>Use-context flags you log, sterilization heat, dishwasher cycles, age.</>,
          <>Cited published research behind each 0–100 score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan baby gear and pregnancy products before buying"
        sub="Bottles, sippy cups, baby food pouches, cosmetics. The app weighs material + brand + condition and suggests cleaner-packaged alternatives."
        buttonLabel="Scan baby gear in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Do microplastics cause preeclampsia?',
            answer:
              'Microplastic particles themselves have not been shown to directly cause preeclampsia in humans. However, the plasticisers carried by them (PFAS, BPA, phthalates) have been linked in multiple human cohort studies to higher preeclampsia risk through endothelial dysfunction, oxidative stress, and impaired placental development. Animal studies show that microplastic exposure damages placental function.',
          },
          {
            question: 'Which plastic chemicals have the strongest link to preeclampsia?',
            answer:
              'PFAS (the "forever chemicals" in non-stick cookware, water, and stain-resistant products) have the strongest evidence, with multiple human cohorts showing elevated preeclampsia risk at higher maternal serum levels. BPA and phthalates (DEHP, DBP) also show consistent associations through impaired trophoblast function.',
          },
          {
            question: 'I have preeclampsia. Was it caused by my exposure to plastic?',
            answer:
              'Preeclampsia is multifactorial. Major risk factors are not exposure-related, first pregnancy, age, family history, chronic hypertension, diabetes, obesity, IVF. Environmental exposures appear to add a small additional risk on top. Your preeclampsia was not "caused" by plastic in any individual sense, but reducing exposures going forward is reasonable, especially if you may have future pregnancies.',
          },
          {
            question: 'What is the single highest-impact plastic-reduction step for preeclampsia risk?',
            answer:
              'Filtering water with a PFAS-rated filter (Clearly Filtered, Epic, AquaTru, reverse osmosis). PFAS has the strongest preeclampsia signal among plasticisers and persists in the body for years. Standard pitcher filters do not adequately remove PFAS, look for activated carbon block, RO, or ion exchange certified for PFAS reduction.',
          },
          {
            question: 'Should I take low-dose aspirin?',
            answer:
              'Talk to your OB. ACOG recommends low-dose aspirin (81 mg) starting around 12 weeks for women with one major or two moderate risk factors for preeclampsia. It is evidence-based and reduces preeclampsia risk by about 10-20% in high-risk women. This decision is medical, not lifestyle.',
          },
          {
            question: 'Can plastic reduction reverse preeclampsia once diagnosed?',
            answer:
              'No. Once preeclampsia is diagnosed, treatment is medical, blood pressure management, monitoring, and ultimately delivery of the baby. Plastic reduction is a preventive strategy for future pregnancies, not a treatment for current preeclampsia.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Stein CR, Savitz DA, Dougan M',
            year: 2009,
            title: 'Serum levels of perfluorooctanoic acid and perfluorooctane sulfonate and pregnancy outcome',
            publisher: 'American Journal of Epidemiology',
            url: 'https://academic.oup.com/aje/article/170/7/837/110984',
          },
          {
            authors: 'Cantonwine D, Meeker JD, Hu H, et al.',
            year: 2010,
            title: 'Bisphenol A exposure in Mexico City and risk of prematurity',
            publisher: 'Environmental Health',
            url: 'https://ehjournal.biomedcentral.com/articles/10.1186/1476-069X-9-62',
          },
          {
            authors: 'ACOG Practice Bulletin 222',
            year: 2020,
            title: 'Gestational Hypertension and Preeclampsia',
            publisher: 'American College of Obstetricians and Gynecologists',
            url: 'https://www.acog.org/clinical/clinical-guidance/practice-bulletin/articles/2020/06/gestational-hypertension-and-preeclampsia',
          },
          {
            authors: 'Borghese MM, Walker M, Helewa ME, et al.',
            year: 2020,
            title: 'Association of perfluoroalkyl substances with gestational hypertension and preeclampsia',
            publisher: 'Environmental International',
            url: 'https://pubmed.ncbi.nlm.nih.gov/32891909/',
          },
          {
            authors: 'Garcia MA, Liu R, Nihart A, et al.',
            year: 2024,
            title: 'Quantitation and identification of microplastics in human placental specimens',
            publisher: 'Toxicological Sciences',
            url: 'https://academic.oup.com/toxsci/article/199/1/81/7609299',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
